import { Text } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import type { ResponseOf } from "../../../../../core/api";
import useApi from "../../../../../shared/hooks/useApi";
import type { PatientId } from "../../../../../shared/schema/schema";
import EntityProfileDataTable from "../../../../entityProfileModal/components/EntityProfileDataTable";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import { PatientProfilePatient } from "../../../patientProfileModal.types";

type Props = {
  contracts: PatientProfilePatient["contracts"];
  patientId: PatientId;
};

type PatientStatusChange = ResponseOf<
  "get",
  "./patients/:patientId/patient_statuses/patient_status_change"
>["list"][number];

function formatStatusLabel(value: string) {
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, "");
}

function formatDate(value: LocalDate | string | null) {
  if (value === null || value === "") {
    return "";
  }

  if (value instanceof LocalDate) {
    return value.toString();
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function getPatientContractName(
  contracts: PatientProfilePatient["contracts"],
  patientContractId: number | null
) {
  if (patientContractId === null) {
    return contracts[0]?.contractTypeName ?? "";
  }

  return (
    contracts.find((contract) => contract.id === patientContractId)?.contractTypeName ??
    `Contract #${patientContractId}`
  );
}

function formatReason(row: PatientStatusChange) {
  return (
    row.reasonLabel ??
    row.statusChangeReasonText ??
    (row.reason == null ? "" : `Reason #${row.reason}`)
  );
}

function formatTo(row: PatientStatusChange) {
  return row.toLabel ?? row.statusChangeToText ?? (row.to == null ? "" : `To #${row.to}`);
}

export default function PatientProfileStatusReviewSection(props: Props) {
  const { api } = useApi();
  const statusChangesQuery = useQuery({
    queryKey: ["patientProfileModal", props.patientId, "statusChanges"],
    queryFn: () =>
      api.get("./patients/:patientId/patient_statuses/patient_status_change", {
        path: { patientId: props.patientId },
      }),
  });

  const rows = statusChangesQuery.data?.list ?? [];

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load status review."
      isError={statusChangesQuery.isError}
      isLoading={statusChangesQuery.isLoading}
      loadingLabel="Loading status review"
    >
      <EntityProfileDataTable
        columns={[
          {
            header: "Status",
            key: "status",
            render: (row) =>
              row.statusDisplayName || row.statusText || formatStatusLabel(row.status),
            sortKey: "status",
            sortValue: (row) =>
              row.statusDisplayName || row.statusText || formatStatusLabel(row.status),
          },
          {
            header: "Contract",
            key: "contract",
            render: (row) => getPatientContractName(props.contracts, row.patientContractId),
            sortKey: "contract",
            sortValue: (row) => getPatientContractName(props.contracts, row.patientContractId),
          },
          {
            header: "Effective date",
            key: "effectiveDate",
            render: (row) => formatDate(row.effectiveDate),
            sortKey: "effectiveDate",
            sortValue: (row) => row.effectiveDate?.toString() ?? "",
          },
          {
            header: "Reason",
            key: "reason",
            render: (row) => formatReason(row),
            sortKey: "reason",
            sortValue: (row) => formatReason(row),
          },
          {
            header: "To",
            key: "to",
            render: (row) => formatTo(row),
            sortKey: "to",
            sortValue: (row) => formatTo(row),
          },
          {
            header: "Note",
            key: "note",
            render: (row) => (
              <>
                <Text minH="19px">{row.notes ? stripHtml(row.notes) : ""}</Text>
                {row.predefinedAnswerText ? (
                  <Text color={entityProfileModalColors.secondaryText} fontSize="12px">
                    Reason: {row.predefinedAnswerText}
                  </Text>
                ) : null}
              </>
            ),
            sortKey: "note",
            sortValue: (row) =>
              `${row.notes ? stripHtml(row.notes) : ""} ${row.predefinedAnswerText ?? ""}`,
          },
        ]}
        emptyLabel="No status changes."
        getRowKey={(row, index) => `${row.status}-${row.effectiveDate}-${index}`}
        pageSizeOptions={[10, 25, 50, 100]}
        rows={rows}
      />
    </EntityProfileQuerySection>
  );
}
